import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { TOUR_STEPS } from "@/lib/tourSteps";
import { Button } from "@/components/ui/button";

// Visite guidée interactive : entoure de vrais éléments (pas de voile bloquant —
// l'utilisateur peut cliquer sur l'interface pendant la visite) et navigue entre les pages.
export default function GuidedTour({ onClose }) {
  const { t } = useAuth();
  const lang = localStorage.getItem("rannea_lang") === "en" ? "en" : "fr";
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const step = TOUR_STEPS[idx];
  const content = step[lang];
  const last = idx === TOUR_STEPS.length - 1;
  const selectors = step.targets || (step.target ? [step.target] : []);

  const findEl = () => {
    for (const s of selectors) {
      const el = document.querySelector(`[data-testid="${s}"]`);
      if (el) return el;
    }
    return null;
  };

  // Naviguer vers la page de l'étape
  useEffect(() => {
    if (step.route && location.pathname !== step.route) navigate(step.route);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  // Localiser l'élément cible (quelques essais le temps que la page charge)
  useEffect(() => {
    setRect(null);
    if (!selectors.length) return undefined;
    let tries = 0;
    let cancelled = false;
    const find = () => {
      if (cancelled) return;
      const el = findEl();
      if (el) {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        setTimeout(() => {
          const el2 = findEl();
          if (el2 && !cancelled) setRect(el2.getBoundingClientRect());
        }, 350);
      } else if (tries++ < 15) {
        setTimeout(find, 400);
      }
    };
    const t0 = setTimeout(find, 300);
    return () => { cancelled = true; clearTimeout(t0); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, location.pathname]);

  // Suivre la cible au scroll / redimensionnement
  useEffect(() => {
    if (!selectors.length) return undefined;
    const upd = () => {
      const el = findEl();
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener("scroll", upd, true);
    window.addEventListener("resize", upd);
    return () => {
      window.removeEventListener("scroll", upd, true);
      window.removeEventListener("resize", upd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  const close = () => {
    api.put("/display/prefs", { guided_tour_seen: true }).catch(() => {});
    onClose();
  };

  const tooltipStyle = useMemo(() => {
    const w = Math.min(320, window.innerWidth - 24);
    // Pas de transform ici : l'animation d'entrée l'écraserait (bulle hors écran)
    if (!rect) return { left: Math.max(12, (window.innerWidth - w) / 2), top: 88, width: w };
    const left = Math.max(12, Math.min(rect.left + rect.width / 2 - w / 2, window.innerWidth - w - 12));
    const below = rect.bottom + 12 + 190 < window.innerHeight;
    const top = below ? rect.bottom + 12 : Math.max(12, rect.top - 12 - 190);
    return { left, top, width: w };
  }, [rect]);

  return (
    <div className="fixed inset-0 z-[80] pointer-events-none" data-testid="guided-tour">
      {rect && (
        <div
          className="fixed rounded-xl border-2 border-amber-400 shadow-[0_0_0_4px_rgba(251,191,36,0.25),0_0_24px_rgba(251,191,36,0.35)] animate-pulse"
          style={{ left: rect.left - 6, top: rect.top - 6, width: rect.width + 12, height: rect.height + 12 }}
          data-testid="guided-tour-highlight"
        />
      )}
      <div
        className="fixed app-card p-4 space-y-3 pointer-events-auto shadow-2xl anim-fade-up max-h-[70vh] overflow-y-auto"
        style={tooltipStyle}
        data-testid="guided-tour-tooltip"
      >
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-heading font-bold text-base">{content.title}</h3>
          <button onClick={close} className="t-secondary hover:text-white p-1 -m-1" data-testid="guided-tour-close" aria-label={t("common.close")}>
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-sm t-secondary leading-relaxed">{content.desc}</p>
        <div className="flex items-center gap-2">
          <span className="text-xs t-secondary mr-auto" data-testid="guided-tour-counter">{t("tut.counter", { i: idx + 1, n: TOUR_STEPS.length })}</span>
          {idx > 0 && (
            <Button size="sm" variant="outline" className="border-app h-8" onClick={() => setIdx(idx - 1)} data-testid="guided-tour-prev">
              <ChevronLeft className="h-4 w-4" />
            </Button>
          )}
          <Button size="sm" className="brand-bg text-white h-8" onClick={() => (last ? close() : setIdx(idx + 1))} data-testid="guided-tour-next">
            {last ? t("tour.done") : t("tut.next")}
            {!last && <ChevronRight className="h-4 w-4 ml-1" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
